/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class CustomSortAction
 * @hideconstructor
 * @classdesc
 */
define([
	'../../../../widgets/livewidget/dialogs/CustomSortDialog',
	'../../../../widgets/livewidget/nls/StringResources',
	'../../../../features/dashboard/queryService/QueryServiceUtils'
], (CustomSortDialog, StringResources, QueryServiceUtils) => {

	return class CustomSortAction {

		constructor({ dashboardAPI, options }) {
			this.dashboard = dashboardAPI;
			this.isCustomSortActionEnabled = !(options && options.customSort===false);
		}

		/**
		 * Returns true if custom sort should be visible for the selected slot and index. Custom sort should only be enabled when:
		 * 1) For true attribute data which means both the dataItem AND the metadata column its bound to are type 'attribute'
		 * 		(ie: can't custom sort a fact bound to a category slot)
		 * 2) The action is not disabled in the perspective file.
		 * @param {object} dataSlot
		 * @param {number} itemIndex
		 */
		isEnabled(dataSlot, itemIndex) {
			const dataItemList = dataSlot.getDataItemList();
			const dataItem = dataItemList && dataItemList[itemIndex];
			return this.isCustomSortActionEnabled &&
				dataItem &&
				dataItem.getType() === 'attribute' &&
				dataItem.getMetadataColumn() &&
				dataItem.getMetadataColumn().getType() === 'attribute';
		}

		/**
		 * Returns the menu item for the sort menu
		 * @returns {object}
		 */
		getMenuItem() {
			return {
				id: 'custom',
				localizedString: StringResources.get('toolbarActionSortCustom')
			};
		}

		/**
		 * Handles the action to initiate creating or editing a custom sort
		 * @param {object} dataItemAPI
		 * @param {string} defaultSortOrder the string describing the default sort order
		 */
		handleSelection(dataItem, defaultSortOrder, defaultSortOrderRaw) {
			this.defaultSortOrderRaw = defaultSortOrderRaw;
			const dialog = new CustomSortDialog({
				label: dataItem.getLabel(),
				sortSpec: dataItem.getSort(),
				sortType: this._getSortType(dataItem),
				defaultSortOrder: defaultSortOrder,
				getItems: this._getDataItemsFromSearchString.bind(this, dataItem),
				getItemsFromValues: this._getDataItemsFromUseValues.bind(this, dataItem),
				apply: this._applyCustomSort.bind(this, dataItem),
				logger: this.dashboard.getGlassCoreSvc('.Logger'),
				iconsFeature: this.dashboard.getFeature('Icons')
			});

			dialog.render();
		}

		/**
		 * Returns a promise resolving to the items matching the given queryContext. If no queryContext
		 * is provided, all items will be returned.
		 * To be supplied for consumption by customSortDialog whenever a query is needed
		 * @param {object} dataItemApi data item api for selected slot & index
		 * @param {string} queryContext query context
		 * @returns {object} a Promise
		 */
		_getDataItems(dataItem, queryContext) {
			const metadataColumn = dataItem.getMetadataColumn();
			const sortObj = dataItem.getSort();
			const queryService = this.dashboard.getFeature('QueryService');
			const searchTermList = (queryContext && queryContext.searchTerm) ? [queryContext.searchTerm]: [];
			const sortType = (sortObj && sortObj.type) || this._getDefaultSort();
			return queryService.getColumnValues(metadataColumn.getSourceId(), [metadataColumn.getId()], [], sortType, searchTermList)
				.then((queryResults)=>{
					return QueryServiceUtils.extractListOfQueryResultData(queryResults);
				});

		}

		/**
		 * Returns the default sort to be used in the list.
		 * When custom sort is in 'auto' mode we do not have a type, which causes dataItem.getSort().type to be undefined.
		 * We need a sort type for the query, so we use the defaultSortOrder or, in case it's not ascending or descending, we default do ascending.
		 * @returns {String} sort order
		 */
		_getDefaultSort() {
			return this.defaultSortOrderRaw === 'asc' || this.defaultSortOrderRaw === 'desc' ? this.defaultSortOrderRaw : 'asc';
		}

		/**
		 * Returns a promise resolving to the items matching the given searchString. If no searchString
		 * is provided, all items will be returned.
		 * Used by customSortDialog whenever a search is made in search bar.
		 * @param {object} dataItemApi data item api for selected slot & index
		 * @param {string} searchString search string to match
		 * @returns {object} a Promise
		 */
		_getDataItemsFromSearchString(dataItem, searchString) {
			const queryContext = searchString && { searchTerm: searchString } || undefined;
			return this._getDataItems(dataItem, queryContext);
		}

		/**
		 * Returns the sort type for sortRemainingItemsBy attribute of CustomSortDialog
		 * @param {object} dataItemApi data item api for selected slot & index
		 * @returns {String} type
		 */
		_getSortType(dataItem) {
			if (dataItem.hasDefaultSort()) {
				return 'auto';
			} else {
				const sortSpec = dataItem.getSort();
				if (sortSpec.custom && sortSpec.custom.sortRemainingItemsBy) {
					return sortSpec.custom.sortRemainingItemsBy;
				} else {
					return sortSpec && sortSpec.type || 'auto';
				}
			}
		}

		/**
		 * Returns a promise resolving to the items matching the given useValues. If no useValues
		 * is provided, all items will be returned.
		 * Used by customSortDialog for querying items based on use values
		 * eg. when custom sort saves use value of item outside range of query limit
		 * @param {object} dataItem data item api for selected slot & index
		 * @param {useValues} useValues list of use values in {u,d} format
		 * @returns {object} a Promise
		 */
		_getDataItemsFromUseValues(dataItem, useValues) {
			const queryContext = useValues? {
				filter: {
					expression: {
						operator:'in',
						values: useValues,
						itemId: dataItem.getColumnId()
					},
					type: 'pre'
				}
			} : undefined;

			return this._getDataItems(dataItem, queryContext);
		}

		/**
		* Applies the given custom sort properties.
		* example spec
		*			sortSpec = {
		*					"type": "asc",
		*					"custom": {
		*							"enabled": true,
		*							"top": [
		*									"Sheet1.Genre->[Drama]",
		*									"Sheet1.Genre->[Comedy]",
		*									"Sheet1.Genre->[Biopic]"
		*							],
		*                           "sortRemainingItemsBy": "auto"
		*					}
		*			}
		* @param {object} dataItem data item api for selected slot & index
		* @param {object} properties
		*/
		_applyCustomSort(dataItem, properties) {
			const sortSpec = properties? {
				type: properties.type === 'auto' ? undefined : properties.type,
				custom: {
					enabled: true,
					top: properties.top,
					sortRemainingItemsBy: properties.type
				}
			} : {
				type: null
			};
			dataItem.setSort(sortSpec);
		}

	};
});
